import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Checkbox,
  Divider,
  Popover,
  Typography,
  withStyles,
} from '@material-ui/core'
import { CoreButton } from 'src/components/core'
import { CardPublishState } from 'src/hooks/useCoursePublishState'
import styles from './styles'

/**
 * The panel behind the top bar's "Publish changes" button.
 *
 * New pages and edited pages are separate groups on purpose: the whole point of the panel is that a
 * user can ship edits to live pages while leaving unfinished new pages as drafts.
 */
const PublishChangesPopover = props => {
  const { anchorEl, onClose, pendingPages, onPublish, classes } = props

  const newPages = useMemo(
    () => pendingPages.filter(page => page.state === CardPublishState.DRAFT),
    [pendingPages],
  )

  const editedPages = useMemo(
    () =>
      pendingPages.filter(
        page => page.state === CardPublishState.PUBLISHED_WITH_CHANGES,
      ),
    [pendingPages],
  )

  const [selected, setSelected] = useState([])
  const [publishing, setPublishing] = useState(false)

  const open = Boolean(anchorEl)

  // pendingPages is rebuilt from scratch on every refresh, and the editor refreshes on every
  // autosave. Keying the reset on the array itself re-ticked everything mid-decision — which is
  // how a page the user had deliberately unticked could still go live.
  const pendingKey = pendingPages.map(page => page.cardId).join(',')

  // Everything starts ticked — the common case is shipping all of it, and unticking is the
  // deliberate act. Re-tick when the panel opens, or when the pending set genuinely changes.
  useEffect(() => {
    if (open) {
      setSelected(pendingPages.map(page => page.cardId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingKey])

  const toggle = cardId =>
    setSelected(current =>
      current.includes(cardId)
        ? current.filter(id => id !== cardId)
        : [...current, cardId],
    )

  const toggleGroup = pages => {
    const ids = pages.map(page => page.cardId)
    const allSelected = ids.every(id => selected.includes(id))

    setSelected(current =>
      allSelected
        ? current.filter(id => !ids.includes(id))
        : [...new Set([...current, ...ids])],
    )
  }

  const handlePublish = async () => {
    setPublishing(true)

    try {
      await onPublish(selected)
      onClose()
    } finally {
      setPublishing(false)
    }
  }

  const renderGroup = (label, pages, badgeLabel) => {
    if (pages.length === 0) {
      return null
    }

    const ids = pages.map(page => page.cardId)
    const selectedCount = ids.filter(id => selected.includes(id)).length

    return (
      <div className={classes.group}>
        <div className={classes.groupHeader}>
          <Checkbox
            size="small"
            color="primary"
            checked={selectedCount === ids.length}
            indeterminate={selectedCount > 0 && selectedCount < ids.length}
            onChange={() => toggleGroup(pages)}
          />
          <Typography className={classes.groupLabel}>{label}</Typography>
        </div>

        {pages.map(page => (
          <div className={classes.row} key={page.cardId}>
            <Checkbox
              size="small"
              color="primary"
              checked={selected.includes(page.cardId)}
              onChange={() => toggle(page.cardId)}
            />
            <Typography className={classes.pageTitle} noWrap>
              {page.title || 'Untitled page'}
            </Typography>
            <Typography
              component="span"
              className={
                badgeLabel === 'Draft' ? classes.draftBadge : classes.editedBadge
              }
            >
              {badgeLabel}
            </Typography>
          </div>
        ))}
      </div>
    )
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      style={{ marginTop: '8px' }}
    >
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.title}>Publish changes</Typography>
          <Typography className={classes.count}>
            {pendingPages.length} {pendingPages.length === 1 ? 'item' : 'items'}
          </Typography>
        </div>

        {renderGroup('New pages', newPages, 'Draft')}
        {renderGroup('Edited pages', editedPages, 'Edited')}

        <Divider className={classes.divider} />

        <div className={classes.footer}>
          <Typography className={classes.footnote}>
            Theme and settings are always live.
          </Typography>
          <CoreButton
            size="small"
            variant="contained"
            color="primary"
            disabled={selected.length === 0 || publishing}
            onClick={handlePublish}
          >
            {publishing ? 'Publishing…' : `Publish ${selected.length}`}
          </CoreButton>
        </div>
      </div>
    </Popover>
  )
}

PublishChangesPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  pendingPages: PropTypes.array.isRequired,
  onPublish: PropTypes.func.isRequired,
}

export default withStyles(styles)(PublishChangesPopover)
